import { useState } from "react";
import { TextInput, View, Text } from "react-native";
import {
  Dialog,
  DialogIcon,
  DialogPrimaryButton,
  SoftSecondaryButton,
  haptic,
} from "@louise/moon-ui";
import { EMBER } from "../../../../onboarding/theme";
import {
  use2FAStatus,
  useDisable2FA,
  useDisablePasskey,
} from "@src/packages/auth/hooks/use2FA";
import { useTranslations } from "@src/packages/i18n";

const padlockIcon =
  require("../../../../assets/images/you-icons/padlock.png") as number;

type Props = {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export default function Disable2FAModal({
  visible,
  onClose,
  onConfirm,
}: Props) {
  const t = useTranslations();
  const disable2FA = useDisable2FA();
  const disablePasskey = useDisablePasskey();
  // TOTP disable takes a fresh code; passkey disable re-authenticates the
  // passkey instead (no code input). Route by which factor the user has.
  const { data: status } = use2FAStatus();
  const isTotp = status?.totpEnrolled ?? false;
  const [totpCode, setTotpCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setTotpCode("");
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDisable = async () => {
    try {
      setError(null);
      if (isTotp) {
        await disable2FA.mutateAsync({ totpCode });
      } else {
        await disablePasskey.mutateAsync();
      }
      haptic("buzz");
      reset();
      onConfirm();
    } catch (err) {
      const msg = (err instanceof Error ? err.message : "").toLowerCase();
      const isAuthError = msg.includes("invalid") || msg.includes("not found");
      setError(
        t(
          isAuthError
            ? "you.modals.disable_2fa.error_invalid"
            : "you.modals.disable_2fa.error_generic",
        ),
      );
    }
  };

  const isValid = !isTotp || totpCode.length === 6;

  return (
    <Dialog visible={visible} onClose={handleClose} layout="centered">
      <DialogIcon
        source={padlockIcon}
        width={80}
        height={88}
        testID="disable-2fa-padlock"
      />
      <Text
        accessibilityRole="header"
        className="font-dm-sans"
        style={{
          fontSize: 20,
          fontWeight: "700",
          color: EMBER.colors.text,
          textAlign: "center",
          marginTop: 14,
          marginBottom: 8,
        }}
      >
        {t("you.modals.disable_2fa.title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          color: EMBER.colors.textLight,
          textAlign: "center",
          lineHeight: 20,
          marginBottom: 16,
        }}
      >
        {t(
          isTotp
            ? "you.modals.disable_2fa.message"
            : "you.modals.disable_2fa.passkey_message",
        )}
      </Text>
      {isTotp && (
        <TextInput
          value={totpCode}
          onChangeText={(text) => {
            setTotpCode(text.replace(/\D/g, "").slice(0, 6));
            setError(null);
          }}
          placeholder={t("you.modals.disable_2fa.code_placeholder")}
          keyboardType="number-pad"
          maxLength={6}
          autoFocus
          style={{
            fontSize: 24,
            fontWeight: "600",
            textAlign: "center",
            letterSpacing: 8,
            paddingVertical: 12,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: error ? EMBER.colors.coral : EMBER.colors.border,
            color: EMBER.colors.text,
            marginBottom: error ? 8 : 16,
          }}
        />
      )}
      {error && (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            color: EMBER.colors.coral,
            textAlign: "center",
            marginBottom: 16,
          }}
        >
          {error}
        </Text>
      )}
      <View style={{ flexDirection: "row", gap: 12 }}>
        <SoftSecondaryButton
          label={t("you.modals.disable_2fa.cancel")}
          onPress={handleClose}
          flex={1}
        />
        <DialogPrimaryButton
          label={t("you.modals.disable_2fa.confirm")}
          onPress={handleDisable}
          loading={disable2FA.isPending || disablePasskey.isPending}
          disabled={!isValid}
          flex={1}
        />
      </View>
    </Dialog>
  );
}
